import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Cloud, ExternalLink, Cookie } from 'lucide-react';
import { useCookieConsent } from '../context/CookieContext';
import { ThemeToggle } from './ThemeToggle';

const StatusDeckWidget = () => {
  const containerRef = useRef(null);
  const [isDark, setIsDark] = React.useState(
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear previous content
    containerRef.current.innerHTML = '';

    // Create widget div
    const widgetDiv = document.createElement('div');
    widgetDiv.setAttribute('data-statusdeck-widget', '');
    widgetDiv.setAttribute('data-slug', 'wolkenhelden');
    widgetDiv.setAttribute('data-style', 'detailed');
    widgetDiv.setAttribute('data-size', 'medium');
    widgetDiv.setAttribute('data-theme', isDark ? 'dark' : 'light');
    widgetDiv.setAttribute('data-show-incidents', 'false');
    widgetDiv.setAttribute('data-custom-text-operational', 'Alle Systeme laufen normal');
    widgetDiv.setAttribute('data-custom-text-degraded', 'Teilweise Performance-Probleme');
    widgetDiv.setAttribute('data-custom-text-partial', 'Ausfall einzelner Dienste');
    widgetDiv.setAttribute('data-custom-text-major', 'Kompletter Ausfall');
    widgetDiv.setAttribute('data-hide-branding', 'true');
    containerRef.current.appendChild(widgetDiv);

    // Load script AFTER widget div is in DOM
    const script = document.createElement('script');
    script.src = 'https://statusdeck.net/statusdeck-widget.js';
    script.async = true;
    containerRef.current.appendChild(script);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [isDark]);

  return <div ref={containerRef} className="mt-4" />;
};

export const Footer = () => {
  const { openSettings } = useCookieConsent();

  return (
    <footer className="border-t bg-muted/30 mt-24" data-testid="footer">
      <div className="container mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-[1.5fr_1fr_1.5fr_1fr]">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <Cloud className="h-6 w-6 text-primary" />
              <span className="font-display text-xl font-semibold">Wolkenhelden</span>
            </Link>
            <p className="text-sm text-muted-foreground mb-4">
              🦸 Managed Nextcloud mit S3-Storage. Heldenhafte Cloud-Power für dein Team.
            </p>
            <button 
              onClick={openSettings} 
              className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
              data-testid="cookie-settings-btn"
            >
              <Cookie className="h-3 w-3" />
              Cookie-Einstellungen
            </button>
            {/* Theme Toggle - nur auf kleineren Bildschirmen sichtbar (im Header bei xl+ versteckt) */}
            <div className="xl:hidden mt-3 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Design:</span>
              <ThemeToggle />
            </div>
            <StatusDeckWidget />
          </div>

          {/* Produkt */}
          <div>
            <h3 className="font-semibold mb-4">🚀 Produkt</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/preise" className="text-muted-foreground hover:text-primary transition-colors">Helden-Preise</Link></li>
              <li><Link to="/features" className="text-muted-foreground hover:text-primary transition-colors">Superkräfte</Link></li>
              <li><Link to="/technik" className="text-muted-foreground hover:text-primary transition-colors">Helden-Technik</Link></li>
              <li><Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors">Helden-FAQ</Link></li>
            </ul>
          </div>

          {/* Rechtliches */}
          <div>
            <h3 className="font-semibold mb-4">📋 Rechtliches</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/agb" className="text-muted-foreground hover:text-primary transition-colors">Allgemeine Geschäftsbedingungen</Link></li>
              <li><Link to="/avv" className="text-muted-foreground hover:text-primary transition-colors">Auftragsverarbeitungsvertrag</Link></li>
              <li><Link to="/sla" className="text-muted-foreground hover:text-primary transition-colors">Service Level Agreement</Link></li>
              <li><Link to="/datenschutz" className="text-muted-foreground hover:text-primary transition-colors">Datenschutz</Link></li>
              <li><Link to="/impressum" className="text-muted-foreground hover:text-primary transition-colors">Impressum</Link></li>
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h3 className="font-semibold mb-4">🦸 Helden-Kontakt</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>Support@Wolkenhelden.de</li>
              <li className="pt-2">Helden-Standorte:</li>
              <li>⚡ Frankfurt am Main</li>
              <li>🔧 Hamburg (in Einrichtung)</li>
            </ul>
          </div>
        </div>

        <div className="mt-8 border-t pt-8 text-center text-sm text-muted-foreground">
          <p>
            &copy; {new Date().getFullYear()} Wolkenhelden – Ein Service von{' '}
            <a 
              href="https://www.host-on.de/de/impressum" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-medium hover:text-primary transition-colors inline-flex items-center gap-1"
            >
              Host-On Service Provider GmbH
              <ExternalLink className="h-3 w-3" />
            </a>
          </p>
          <p className="mt-2 text-xs">
            Made with 💙 in Germany 🇩🇪
          </p>
          <p className="mt-2 text-xs text-muted-foreground/70">
            Alle Preise inkl. 19% MwSt.
          </p>
        </div>
      </div>
    </footer>
  );
};
